<script lang="ts">
  import { fromPairs, get } from 'lodash';
  import { TextField, Button } from 'svelte-materialify';
  import type {
    CategoryConfig,
    CategoryPropConfig,
  } from '../../modules/projects/project.interface';
  import type { Item } from '../../modules/item/item.interface';
  import { CategoryPropType } from '../../modules/projects/project.interface';
  import ItemPropInput from './ItemPropInput.svelte';
  import { CrawlerService } from '../../modules';
  import { wrapProgress } from '../../store';
  import { UiError } from '../../lib/errors';
  import ItemProductsInput from './ItemProductsInput.svelte';

  export let item: Item;
  export let categoryConfig: CategoryConfig;
  export let isValid = false;
  export let isEdit = true;

  let crawlMetadataUrl: string;
  let messages = {};

  $: onItemChange(item);

  function onItemChange(item: Item) {
    const { props, details = [] } = categoryConfig;

    if (details.length && !item.details) {
      item.details = {};
    }

    const propMessages = props.map((categoryPropConfig) =>
      isPropValid(categoryPropConfig)
    );
    const detailMessages = details.map((categoryPropConfig) =>
      isPropValid(categoryPropConfig, 'details')
    );

    messages = fromPairs([...propMessages, ...detailMessages]);

    isValid = isItemsValid();
  }

  function isItemsValid() {
    return Object.values(messages).every((message) => message === 'valid');
  }

  function valid(isValid: boolean, message: string) {
    return isValid ? 'valid' : message;
  }

  function validate(categoryPropConfig: CategoryPropConfig, value: any) {
    const isRequired = (message = 'Required!') => (v: any) => valid(!!v, message);
    const isNumber = (message = 'Must be number') => (v: any) =>
      valid(!isNaN(Number(v)), message);
    const hasMinimumColors = (minCount = 3) => (v: string[]) =>
      valid(v?.length >= minCount, `At least ${minCount} colors expected`);
    const hasMinimumCategory = (minCount = 1) => (v: string[]) =>
      valid(v?.length >= minCount, `At least ${minCount} category expected`);

    const validations = {
      [CategoryPropType.TEXT]: isRequired(),
      [CategoryPropType.NUMBER]: isNumber(),
      [CategoryPropType.IMAGE]: isRequired(),
      [CategoryPropType.URL]: isRequired(),
      [CategoryPropType.COLORS]: hasMinimumColors(
        categoryPropConfig?.requirements?.minCount
      ),
      [CategoryPropType.BOOLEAN]: () => valid(true, ''),
      [CategoryPropType.ARTICLE_CATEGORY]: hasMinimumCategory(),
    };

    return validations[categoryPropConfig.type](value);
  }

  function isPropValid(categoryPropConfig: CategoryPropConfig, path?: string) {
    const { key, isOptional } = categoryPropConfig;

    const valuePath = `${path ? path + '.' : ''}${key}`;
    const value = get(item, valuePath);
    const valid = !!isOptional ? 'valid' : '';

    return [valuePath, valid || validate(categoryPropConfig, value)];
  }

  function getMessage(_: Item, propKey: string, path?: string) {
    return get(messages, `${path ? path + '.' : ''}${propKey}`);
  }

  async function fetchMetadata() {
    if (!crawlMetadataUrl) {
      throw new UiError('Please enter a metadata url!');
    }

    const { title, url, imageUrl } = await CrawlerService.crawlMetadata(crawlMetadataUrl);

    item.title = title;
    item.url = url || crawlMetadataUrl;
    item.image = imageUrl;
    crawlMetadataUrl = '';
  }
</script>

{#if !isEdit && categoryConfig.crawlMeta}
  <div class="d-flex mb-8">
    <TextField bind:value={crawlMetadataUrl} outlined>
      <span> Enter URL to fetch metadata </span>
    </TextField>
    <div class="ml-4">
      <Button
        class="green white-text"
        on:click={wrapProgress(fetchMetadata)}
        size="x-large">Fetch From OG</Button
      >
    </div>
  </div>
{/if}
{#if categoryConfig.key === 'product'}
  {#each categoryConfig.props as categoryPropConfig}
    <ItemPropInput
      on:fileAdded
      {categoryPropConfig}
      bind:value={item[categoryPropConfig.key]}
      message={getMessage(item, categoryPropConfig.key)}
    />
  {/each}
  {#each categoryConfig.details as categoryPropConfig}
    <ItemProductsInput
      {categoryPropConfig}
      path="details"
      on:fileAdded
      bind:value={item.details[categoryPropConfig.key]}
      bind:values={item.details[categoryPropConfig.key]}
      message={getMessage(item, categoryPropConfig.key, 'details')}
    />
  {/each}
{:else}
  {#each categoryConfig.props as categoryPropConfig}
    <ItemPropInput
      on:fileAdded
      {categoryPropConfig}
      bind:value={item[categoryPropConfig.key]}
      message={getMessage(item, categoryPropConfig.key)}
    />
  {/each}
  {#if categoryConfig.details}
    {#each categoryConfig.details as categoryPropConfig}
      <ItemPropInput
        {categoryPropConfig}
        path="details"
        on:fileAdded
        bind:value={item.details[categoryPropConfig.key]}
        message={getMessage(item, categoryPropConfig.key, 'details')}
      />
    {/each}
  {/if}
{/if}
